import { observeResize } from '@affine/component';
import { AppSidebarService } from '@affine/core/modules/app-sidebar';
import { WorkbenchService } from '@affine/core/modules/workbench';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useRef } from 'react';

let OBSERVED = false;

export const RESPONSIVE_SIDEBAR_HIDE_THRESHOLD = 540;
export const RESPONSIVE_SIDEBAR_FLOAT_THRESHOLD = 900;

export const useResponsiveSidebar = (
  hideThreshold = RESPONSIVE_SIDEBAR_HIDE_THRESHOLD,
  floatThreshold = RESPONSIVE_SIDEBAR_FLOAT_THRESHOLD
) => {
  const previousWidthRef = useRef<number | null>(null);

  const appSidebarService = useService(AppSidebarService);
  const workbenchService = useService(WorkbenchService);

  const handleHideSidebar = useCallback(() => {
    appSidebarService.sidebar.setOpen(false);
    workbenchService.workbench.setSidebarOpen(false);
  }, [appSidebarService, workbenchService]);

  const handleFloatSidebar = useCallback(
    (float: boolean) => {
      appSidebarService.sidebar.setSmallScreenMode(float);
    },
    [appSidebarService.sidebar]
  );

  useEffect(() => {
    if (OBSERVED) {
      console.warn(
        `"${useResponsiveSidebar.name}" already observed, do not call it multiple times`
      );
      return;
    }

    OBSERVED = true;
    const unobserve = observeResize(document.body, entry => {
      if (BUILD_CONFIG.isMobileEdition) {
        return;
      }

      const width = entry.contentRect.width;
      const previousWidth = previousWidthRef.current;
      const floating = width <= floatThreshold;

      if (previousWidth === null) {
        previousWidthRef.current = width;
        if (width <= hideThreshold) {
          handleHideSidebar();
        }
        if (!BUILD_CONFIG.isElectron) {
          handleFloatSidebar(floating);
        }
        return;
      }

      // should hide sidebar
      if (width <= hideThreshold && previousWidth > hideThreshold) {
        handleHideSidebar();
      }

      if (!BUILD_CONFIG.isElectron) {
        const wasFloating = previousWidth <= floatThreshold;
        if (floating !== wasFloating) {
          handleFloatSidebar(floating);
        }
      }

      previousWidthRef.current = width;
    });

    return () => {
      OBSERVED = false;
      unobserve();
    };
  }, [floatThreshold, handleFloatSidebar, handleHideSidebar, hideThreshold]);
};
